import React, { useState, useEffect } from 'react';
import { 
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Card,
  CardBody,
  CardHeader,
  Heading,
  IconButton,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  TableContainer,
  useToast,
  Tooltip,
} from '@chakra-ui/react';
import { CheckIcon, CloseIcon } from '@chakra-ui/icons';
import { useSupabaseClient, useUser } from '@supabase/auth-helpers-react';

export default function RoleRequests() {
  const supabase = useSupabaseClient();
  const user = useUser();
  const [roleRequests, setRoleRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    if (!user) return;
    setLoading(true);
    
    try {
      // Fetch pending role requests
      const { data: requestData, error: requestError } = await supabase
        .from('pending_role_requests')
        .select('*')
        .order('requested_at', { ascending: false });

      if (requestError) throw requestError;
      setRoleRequests(requestData || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: 'Error fetching data',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRoleRequest = async (requestId, userId, action) => {
    try {
      // Call the appropriate database function
      const functionName = action === 'approve' ? 'approve_role_request' : 'reject_role_request';
      const { error } = await supabase.rpc(functionName, {
        request_id: requestId,
        reviewer_id: user.id,
        review_notes: `${action === 'approve' ? 'Approved' : 'Rejected'} via admin dashboard`
      });

      if (error) throw error;

      // Refresh the data
      fetchData();

      toast({
        title: `Role request ${action}ed`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error(`Error ${action}ing role request:`, error);
      toast({
        title: `Error ${action}ing role request`,
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Box p={4}>
      <Card>
        <CardHeader>
          <Heading size="lg">Role Requests</Heading>
        </CardHeader>
        <CardBody>
          <TableContainer>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>User</Th>
                  <Th>Requested Role</Th>
                  <Th>Requested At</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {roleRequests.map((request) => (
                  <Tr key={request.request_id}>
                    <Td>
                      <VStack align="start" spacing={1}>
                        <Text>{request.first_name} {request.last_name}</Text>
                        <Text fontSize="sm" color="gray.500">{request.email}</Text>
                      </VStack>
                    </Td>
                    <Td>
                      <Badge colorScheme="blue">
                        {request.requested_role}
                      </Badge>
                    </Td>
                    <Td>{new Date(request.requested_at).toLocaleDateString()}</Td>
                    <Td>
                      <HStack spacing={2}>
                        <Tooltip label="Approve">
                          <IconButton
                            icon={<CheckIcon />}
                            colorScheme="green"
                            size="sm"
                            onClick={() => handleRoleRequest(request.request_id, request.user_id, 'approve')}
                          />
                        </Tooltip>
                        <Tooltip label="Reject">
                          <IconButton
                            icon={<CloseIcon />}
                            colorScheme="red"
                            size="sm"
                            onClick={() => handleRoleRequest(request.request_id, request.user_id, 'reject')}
                          />
                        </Tooltip>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
                {roleRequests.length === 0 && (
                  <Tr>
                    <Td colSpan={4} textAlign="center">No pending role requests</Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </CardBody>
      </Card>
    </Box>
  );
}
